/**
 * Provider-agnostic auth hooks.
 *
 * All components import from here instead of directly from @frontegg/react.
 * This is the single abstraction layer for auth — when the provider changes,
 * only this file changes. The 15+ consumer files stay untouched.
 *
 * Provider chain:
 *   1. OIDC (custom domains with oidc_config — no Frontegg involved)
 *   2. Frontegg (main domain — email/password login)
 */
import { useMemo } from 'react';
import { useAuth as useFronteggAuth, useAuthActions as useFronteggAuthActions, useTenantsState as useFronteggTenantsState } from '@frontegg/react';
import { usePliableAuth, usePliableAuthActions, usePliableTenantsState } from '../contexts/PliableAuthContext';
import AuthService from '../services/auth.service';

/** Normalized user shape — provider-agnostic */
export interface AppUser {
  id: string;
  sub: string;
  name: string;
  email: string;
  tenantId: string;
  tenantIds: string[];
  roles: Array<{ key: string; name: string }>;
  accessToken: string;
  profilePictureUrl: string;
  verified: boolean;
  mfaEnrolled: boolean;
  activatedForTenant: boolean;
  tenants: Array<{ tenantId: string; name: string }>;
  customClaims: Record<string, any>;
  permissions: string[];
}

export interface AppAuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: AppUser | undefined;
}

export interface AppAuthActions {
  logout: () => void;
  switchTenant: (params: { tenantId: string }) => void;
}

export interface AppTenantsState {
  tenants: Array<{ tenantId: string; name: string }>;
  loading: boolean;
}

function buildUserFromRaw(raw: any): AppUser {
  return {
    id: raw.id || raw.sub || '',
    sub: raw.sub || raw.id || '',
    name: raw.name || '',
    email: raw.email || '',
    tenantId: raw.tenantId || '',
    tenantIds: raw.tenantIds || [raw.tenantId],
    roles: (raw.roles || []).map((r: any) =>
      typeof r === 'string' ? { key: r, name: r } : r
    ),
    accessToken: raw.accessToken || '',
    profilePictureUrl: raw.profilePictureUrl || '',
    verified: raw.verified ?? true,
    mfaEnrolled: raw.mfaEnrolled ?? false,
    activatedForTenant: raw.activatedForTenant ?? true,
    tenants: raw.tenants || [],
    customClaims: raw.customClaims || {},
    permissions: raw.permissions || [],
  };
}

// Which auth provider this app is running against. Hooks can't be called
// conditionally, so this decides *once* which hooks module every consumer
// branches into; embed sessions always take the Pliable path even on non-OIDC
// tenants (Frontegg isn't part of the /auth-exchange handoff). Must match the
// provider choice in src/App.tsx.
//
// Resolved on first use rather than at import. `AuthService.getInstance()` has
// side effects — it reads env config, and in local dev fetches it over a
// blocking synchronous XHR — so evaluating this at module scope meant merely
// *importing* anything downstream of this file could stall. Cached after the
// first call, so the branch never changes identity between renders.
let isPliableAuthCache: boolean | undefined;

function isPliableAuth(): boolean {
  if (isPliableAuthCache === undefined) {
    isPliableAuthCache =
      AuthService.getInstance().oidcEnabled
      || (typeof window !== 'undefined' && window.location.pathname === '/embed');
  }
  return isPliableAuthCache;
}

// ─── Hooks ───────────────────────────────────────────────────────────────────

export function useAppAuth(): AppAuthState {
  if (isPliableAuth()) {
    return usePliableAppAuth();
  }
  return useFronteggAppAuth();
}

function usePliableAppAuth(): AppAuthState {
  const pliable = usePliableAuth();
  // Memoize so consumers using `user` in effect deps / memo deps don't see
  // a fresh object reference on every render — that was causing infinite
  // re-render loops (buildUserFromRaw returns a new object each call).
  return useMemo<AppAuthState>(() => {
    if (pliable.isAuthenticated && pliable.user) {
      return {
        isAuthenticated: true,
        isLoading: false,
        user: buildUserFromRaw(pliable.user),
      };
    }
    return { isAuthenticated: false, isLoading: pliable.isLoading, user: undefined };
  }, [pliable.isAuthenticated, pliable.isLoading, pliable.user]);
}

function useFronteggAppAuth(): AppAuthState {
  const frontegg = useFronteggAuth();
  return frontegg as any;
}

export function useAppAuthActions(): AppAuthActions {
  if (isPliableAuth()) {
    return usePliableAuthActions();
  }
  return useFronteggAuthActions();
}

export function useAppTenantsState(): AppTenantsState {
  if (isPliableAuth()) {
    return usePliableTenantsState();
  }
  return useFronteggTenantsState() as any;
}
